import { EAnticipationStatus } from 'interfaces/IUser';
import type IUser from 'interfaces/IUser';
import type { AccountManager, IUserData } from 'v2/interfaces/user';

class User implements IUser {
  public id: number;
  public name: string;
  public full_name: string;
  public business_name: string;
  public producer: boolean;
  public show_legal_person_card: boolean;
  public affiliate: boolean;
  public coproducer: boolean;
  public email: string;
  public beta_customer: boolean;
  public tours: any;
  public type: IUserData['type'];
  public photo: string;
  public eadCharging: boolean;
  public newbie: boolean;
  public is_support: boolean;
  public support_id: number;
  public is_elite: boolean;
  public is_clube_black: boolean;
  public has_launch_export: boolean;
  public has_transfer: boolean;
  public document: string;
  public partial_refund: boolean;
  public coupom_batch: boolean;
  public crossborder: boolean;
  public invoice_transfer: boolean;
  public current_elite_level: any;
  public has_chat_black: boolean;
  public not_recovering: number;
  public new_anticipation_transfer: boolean;
  public subscription_physical: boolean;
  public fiscal_responsibility: number;
  public general_term: boolean;
  public acceptence_terms: boolean;
  public anticipation_status: EAnticipationStatus;
  public anticipation_status_message: string;
  public negociate_invoice: boolean;
  public create_new_account_bank: boolean;
  public overdue_limit: boolean;
  public international_account: boolean;
  public eligible: boolean;
  public account_manager: AccountManager;
  public live_helper_token?: string;
  public cannot_become_partner: boolean;
  public account_manager_id: number;
  public account_currency: IUserData['account_currency'];
  public termsAcceptance: IUserData['termsAcceptance'];
  public show_previous_version_menu: IUserData['show_previous_version_menu'];
  public cell_phone: IUserData['cell_phone'];
  public qualification_key: IUserData['qualification_key'];
  public show_account_owner_change_modal: IUserData['show_account_owner_change_modal'];
  public wallet: IUserData['wallet'];
  public is_agency: IUserData['is_agency'];
  public address: {
    city: string;
    complement: string | null;
    country: string;
    ibge_code: number | null;
    neighborhood: string | null;
    number: string | null;
    state: string;
    street: string | null;
    zip_code: string;
  };

  constructor(userData: IUserData) {
    Object.assign(this, userData);
    this.id = userData.id;
    this.update(userData);
  }

  public update(userData: IUserData) {
    if (userData.id !== this.id) {
      throw new Error('User ID mismatch');
    }

    this.name = userData.name || '';
    this.full_name = userData.full_name || '';
    this.business_name = userData.business_name || '';
    this.producer = userData.producer || false;
    this.affiliate = userData.affiliate || false;
    this.coproducer = userData.coproducer || false;
    this.show_legal_person_card = userData.show_legal_person_card || false;
    this.email = userData.email || '';
    this.beta_customer = userData.beta_customer || false;
    this.type = userData.type;
    this.photo = userData.photo || '';
    this.eadCharging = userData.eadCharging || false;
    this.is_support = userData.is_support || false;
    this.support_id = userData.support_id || 0;
    this.is_elite = userData.is_elite || false;
    this.is_clube_black = userData.is_clube_black || false;
    this.has_transfer = userData.has_transfer || false;
    this.document = userData.document || '';
    this.partial_refund = userData.partial_refund;
    this.coupom_batch = userData.coupom_batch;
    this.crossborder = userData.crossborder || false;
    this.invoice_transfer = userData.invoice_transfer || false;
    this.has_launch_export = userData.has_launch_export || false;
    this.current_elite_level = userData.current_elite_level || false;
    this.has_chat_black = userData.has_chat_black || false;
    this.not_recovering = userData.not_recovering || 0;
    this.new_anticipation_transfer = userData.new_anticipation_transfer || false;
    this.subscription_physical = userData.subscription_physical || false;
    this.newbie = userData.newbie || false;
    this.fiscal_responsibility = userData.fiscal_responsibility || 0;
    this.general_term = userData.general_term || false;
    this.acceptence_terms = userData.acceptence_terms || false;
    this.anticipation_status = userData.anticipation_status || EAnticipationStatus.LIBERADO;
    this.anticipation_status_message = userData.anticipation_status_message || '';
    this.negociate_invoice = userData.negociate_invoice || false;
    this.create_new_account_bank = this.create_new_account_bank || true;
    this.overdue_limit = userData.overdue_limit || false;
    this.international_account = userData.international_account || false;
    this.eligible = userData.eligible || false;
    this.account_manager = userData.account_manager;
    this.is_agency = userData.is_agency;
    this.live_helper_token = userData.live_helper_token || '';
    this.cannot_become_partner = userData.cannot_become_partner || false;
    this.address = userData.address;
    this.account_manager_id = userData.account_manager_id || 0;
    this.account_currency = userData.account_currency || 0;
    this.termsAcceptance = userData.termsAcceptance || false;
    this.show_previous_version_menu = userData.show_previous_version_menu || false;
    this.cell_phone = userData.cell_phone || 0;
    this.qualification_key = userData.qualification_key || '';
    this.show_account_owner_change_modal = userData.show_account_owner_change_modal || false;
    this.wallet = userData.wallet || null;
  }

  public isProducer() {
    return this.producer;
  }

  public isAffiliate() {
    return this.affiliate;
  }

  public isCustomer() {
    return this.beta_customer;
  }

  public isCoproducer() {
    return this.coproducer;
  }

  public isPartner() {
    return this.acceptence_terms;
  }
}

export default User;
